/**
 * The athlete's own plan: read, save and remove. Owner only on the server; a shared viewer gets
 * a refusal, which reads here as "no plan".
 */
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import type { TrainingPlanInput } from '@gd/shared'
import { api } from './client'

export const planKeys = {
  plan: (athleteId: string) => ['plan', athleteId] as const,
}

export function usePlan(athleteId: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: planKeys.plan(athleteId ?? ''),
    queryFn: ({ signal }) =>
      api.get('GET /api/athletes/:id/plan', { params: { id: athleteId! }, signal }),
    enabled: !!athleteId && enabled,
    retry: false,
  })
}

/**
 * What the server derives from the plan (goal, race, blocks, usual week): readiness, the forecast
 * and the coach's weeks. Refetched after a save or delete so the page never shows the old goal.
 */
function invalidatePlanViews(queryClient: QueryClient, athleteId: string) {
  for (const key of ['coach-readiness', 'coach-forecast', 'coach-week'])
    void queryClient.invalidateQueries({ queryKey: [key, athleteId] })
}

export function useSavePlan(athleteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: TrainingPlanInput) =>
      api.put('PUT /api/athletes/:id/plan', { params: { id: athleteId }, body }),
    onSuccess: (plan) => {
      queryClient.setQueryData(planKeys.plan(athleteId), plan)
      invalidatePlanViews(queryClient, athleteId)
    },
  })
}

export function useDeletePlan(athleteId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => api.delete('DELETE /api/athletes/:id/plan', { params: { id: athleteId } }),
    onSuccess: () => {
      queryClient.setQueryData(planKeys.plan(athleteId), null)
      invalidatePlanViews(queryClient, athleteId)
    },
  })
}
